import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useInsightsStore, PostInsights } from '../store/insights';
import { PageHeader } from '../components/PageHeader';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';

// KPI Configuration - labels are resolved dynamically via t()
type KpiKey = 'reach' | 'likeCount' | 'commentsCount' | 'saved' | 'shares' | 'engagementRate' | 'qualityScore';

const kpiColors: Record<KpiKey, string> = {
  reach: '#3B82F6',
  likeCount: '#EC4899',
  commentsCount: '#8B5CF6',
  saved: '#10B981',
  shares: '#F59E0B',
  engagementRate: '#EF4444',
  qualityScore: '#6366F1',
};

const kpiUnits: Record<KpiKey, string> = {
  reach: '',
  likeCount: '',
  commentsCount: '',
  saved: '',
  shares: '',
  engagementRate: '%',
  qualityScore: '',
};

// Custom Tooltip with Post Details
function CustomTooltip({
  active,
  payload,
  label,
  posts,
  t,
  getKpiLabel,
}: {
  active?: boolean;
  payload?: readonly { value: number; dataKey: string; color: string }[];
  label?: string | number;
  posts: PostInsights[];
  t: (key: string) => string;
  getKpiLabel: (kpi: KpiKey) => string;
}) {
  if (!active || !payload || !payload.length) return null;

  const labelStr = String(label);
  const post = posts.find(p => {
    const postDate = new Date(p.timestamp).toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
    });
    return postDate === labelStr;
  });

  return (
    <div className="bg-white border border-gray-200 rounded-lg shadow-lg p-3 max-w-xs">
      {post && (
        <div className="flex items-start gap-3 mb-3 pb-3 border-b border-gray-100">
          {post.thumbnailUrl && (
            <img
              src={post.thumbnailUrl}
              alt=""
              className="w-12 h-12 rounded object-cover flex-shrink-0"
            />
          )}
          <div className="min-w-0 flex-1">
            <p className="text-xs text-gray-500">{labelStr}</p>
            <p className="text-sm text-gray-900 line-clamp-2">
              {post.caption?.slice(0, 60) || t('analytics:noText')}
            </p>
          </div>
        </div>
      )}
      <div className="space-y-1">
        {payload.map((entry, index) => {
          const kpiKey = entry.dataKey as KpiKey;
          return (
            <div key={index} className="flex justify-between text-sm">
              <span style={{ color: entry.color }}>{getKpiLabel(kpiKey)}</span>
              <span className="font-medium">
                {typeof entry.value === 'number'
                  ? entry.value.toLocaleString('de-DE', { maximumFractionDigits: 2 })
                  : entry.value}
                {kpiUnits[kpiKey]}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// Custom X-Axis Tick with Thumbnail
function CustomXAxisTick({
  x,
  y,
  payload,
  posts,
  showThumbnails,
}: {
  x?: number;
  y?: number;
  payload?: { value: string };
  posts: PostInsights[];
  showThumbnails: boolean;
}) {
  if (!payload || x === undefined || y === undefined) return null;

  const post = posts.find(p => {
    const postDate = new Date(p.timestamp).toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
    });
    return postDate === payload.value;
  });

  return (
    <g transform={`translate(${x},${y})`}>
      {showThumbnails && post?.thumbnailUrl ? (
        <foreignObject x={-16} y={5} width={32} height={32}>
          <img
            src={post.thumbnailUrl}
            alt=""
            className="w-8 h-8 rounded object-cover border border-gray-200"
          />
        </foreignObject>
      ) : (
        <text
          x={0}
          y={15}
          textAnchor="middle"
          fill="#6B7280"
          fontSize={11}
        >
          {payload.value}
        </text>
      )}
    </g>
  );
}

export default function AnalyticsTrends() {
  const { t } = useTranslation(['analytics', 'common']);

  // Get KPI label dynamically
  const getKpiLabel = (kpi: KpiKey): string => {
    const labels: Record<KpiKey, string> = {
      reach: t('analytics:metrics.reachShort'),
      likeCount: t('analytics:metrics.likes'),
      commentsCount: t('analytics:metrics.comments'),
      saved: t('analytics:metrics.saves'),
      shares: t('analytics:metrics.shares'),
      engagementRate: t('analytics:metrics.engagementRateShort'),
      qualityScore: t('analytics:metrics.qualityScore'),
    };
    return labels[kpi];
  };

  const {
    posts,
    account,
    isLoading,
    error,
    fetchInsights,
  } = useInsightsStore();

  const [selectedKpis, setSelectedKpis] = useState<KpiKey[]>(['reach', 'qualityScore']);
  const [showThumbnails, setShowThumbnails] = useState(true);

  useEffect(() => {
    fetchInsights(50);
  }, [fetchInsights]);

  // Filter posts from last 30 days and sort by date
  const recentPosts = posts
    .filter(p => p.ageInDays <= 30)
    .sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());

  // Prepare chart data
  const chartData = recentPosts.map(post => ({
    date: new Date(post.timestamp).toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
    }),
    reach: post.reach,
    likeCount: post.likeCount,
    commentsCount: post.commentsCount,
    saved: post.saved,
    shares: post.shares,
    engagementRate: post.engagementRate,
    qualityScore: post.qualityScore,
    post, // Keep reference for tooltip
  }));

  const toggleKpi = (kpi: KpiKey) => {
    setSelectedKpis(prev =>
      prev.includes(kpi)
        ? prev.filter(k => k !== kpi)
        : [...prev, kpi]
    );
  };

  // Calculate averages for summary
  const averages = recentPosts.length > 0 ? {
    reach: Math.round(recentPosts.reduce((sum, p) => sum + p.reach, 0) / recentPosts.length),
    likeCount: Math.round(recentPosts.reduce((sum, p) => sum + p.likeCount, 0) / recentPosts.length),
    commentsCount: Math.round(recentPosts.reduce((sum, p) => sum + p.commentsCount, 0) / recentPosts.length),
    saved: Math.round(recentPosts.reduce((sum, p) => sum + p.saved, 0) / recentPosts.length),
    shares: Math.round(recentPosts.reduce((sum, p) => sum + p.shares, 0) / recentPosts.length),
    engagementRate: (recentPosts.reduce((sum, p) => sum + p.engagementRate, 0) / recentPosts.length).toFixed(2),
    qualityScore: Math.round(recentPosts.reduce((sum, p) => sum + p.qualityScore, 0) / recentPosts.length),
  } : null;

  const kpiKeys: KpiKey[] = ['reach', 'likeCount', 'commentsCount', 'saved', 'shares', 'engagementRate', 'qualityScore'];

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <PageHeader
          icon={
            <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 12l3-3 3 3 4-4M8 21l4-4 4 4M3 4h18M4 4h16v12a1 1 0 01-1 1H5a1 1 0 01-1-1V4z" />
            </svg>
          }
          title={t('analytics:kpiTrends')}
          subtitle={account ? `@${account.username} • ${t('analytics:last30Days')}` : t('analytics:loadingShort')}
          breadcrumbs={[
            { label: t('analytics:breadcrumbs.marketing'), href: '/marketing' },
            { label: t('analytics:breadcrumbs.analytics'), href: '/marketing/analytics' },
            { label: t('analytics:breadcrumbs.trends') },
          ]}
        />

        {error && (
          <div className="mt-6 bg-red-50 border border-red-200 rounded-lg p-4">
            <p className="text-red-800">{error}</p>
          </div>
        )}

        {isLoading ? (
          <div className="mt-8 flex justify-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          </div>
        ) : (
          <>
            {/* Summary Cards */}
            {averages && (
              <div className="mt-6 grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
                {kpiKeys.map(kpi => (
                  <button
                    key={kpi}
                    onClick={() => toggleKpi(kpi)}
                    className={`p-3 rounded-lg border-2 transition-all ${
                      selectedKpis.includes(kpi)
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 bg-white hover:border-gray-300'
                    }`}
                  >
                    <div
                      className="w-3 h-3 rounded-full mb-2"
                      style={{ backgroundColor: kpiColors[kpi] }}
                    />
                    <p className="text-xs text-gray-500">{getKpiLabel(kpi)}</p>
                    <p className="text-lg font-bold text-gray-900">
                      {averages[kpi]}{kpiUnits[kpi]}
                    </p>
                    <p className="text-xs text-gray-400">{t('analytics:trends.avg30Days')}</p>
                  </button>
                ))}
              </div>
            )}

            {/* Controls */}
            <div className="mt-6 flex items-center justify-between">
              <div className="text-sm text-gray-500">
                {recentPosts.length} {t('analytics:trends.postsInLast30Days')}
              </div>
              <label className="flex items-center gap-2 text-sm text-gray-600">
                <input
                  type="checkbox"
                  checked={showThumbnails}
                  onChange={(e) => setShowThumbnails(e.target.checked)}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                {t('analytics:trends.showThumbnails')}
              </label>
            </div>

            {/* Main Chart */}
            <div className="mt-4 bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                {t('analytics:trends.last30DaysTrend')}
              </h3>

              {chartData.length > 0 ? (
                <div style={{ height: showThumbnails ? 450 : 400 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart
                      data={chartData}
                      margin={{ top: 20, right: 30, left: 20, bottom: showThumbnails ? 50 : 30 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                      <XAxis
                        dataKey="date"
                        tick={(props) => (
                          <CustomXAxisTick
                            {...props}
                            posts={recentPosts}
                            showThumbnails={showThumbnails}
                          />
                        )}
                        interval={0}
                        height={showThumbnails ? 50 : 30}
                      />
                      <YAxis
                        yAxisId="left"
                        tick={{ fill: '#6B7280', fontSize: 12 }}
                        tickFormatter={(value) => value.toLocaleString('de-DE')}
                      />
                      {selectedKpis.some(k => k === 'engagementRate' || k === 'qualityScore') && (
                        <YAxis
                          yAxisId="right"
                          orientation="right"
                          tick={{ fill: '#6B7280', fontSize: 12 }}
                          domain={[0, 100]}
                        />
                      )}
                      <Tooltip
                        content={(props) => (
                          <CustomTooltip {...props} posts={recentPosts} t={t} getKpiLabel={getKpiLabel} />
                        )}
                      />
                      <Legend />

                      {selectedKpis.map(kpi => (
                        <Line
                          key={kpi}
                          yAxisId={kpi === 'engagementRate' || kpi === 'qualityScore' ? 'right' : 'left'}
                          type="monotone"
                          dataKey={kpi}
                          name={getKpiLabel(kpi)}
                          stroke={kpiColors[kpi]}
                          strokeWidth={2}
                          dot={{ r: 4, fill: kpiColors[kpi] }}
                          activeDot={{ r: 6, fill: kpiColors[kpi] }}
                        />
                      ))}
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                <div className="h-64 flex items-center justify-center text-gray-500">
                  {t('analytics:trends.noPostsIn30Days')}
                </div>
              )}
            </div>

            {/* Individual KPI Charts */}
            <div className="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-6">
              {selectedKpis.map(kpi => (
                <div key={kpi} className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                  <div className="flex items-center gap-2 mb-4">
                    <div
                      className="w-3 h-3 rounded-full"
                      style={{ backgroundColor: kpiColors[kpi] }}
                    />
                    <h4 className="font-semibold text-gray-900">{getKpiLabel(kpi)}</h4>
                    {averages && (
                      <span className="text-sm text-gray-500 ml-auto">
                        Avg: {averages[kpi]}{kpiUnits[kpi]}
                      </span>
                    )}
                  </div>

                  <div style={{ height: 200 }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart
                        data={chartData}
                        margin={{ top: 10, right: 10, left: 0, bottom: 10 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                        <XAxis
                          dataKey="date"
                          tick={{ fill: '#9CA3AF', fontSize: 10 }}
                          interval="preserveStartEnd"
                        />
                        <YAxis
                          tick={{ fill: '#9CA3AF', fontSize: 10 }}
                          tickFormatter={(value) =>
                            value >= 1000 ? `${(value / 1000).toFixed(1)}k` : value
                          }
                          domain={kpi === 'engagementRate' || kpi === 'qualityScore' ? [0, 100] : ['auto', 'auto']}
                        />
                        <Tooltip
                          content={(props) => (
                            <CustomTooltip {...props} posts={recentPosts} t={t} getKpiLabel={getKpiLabel} />
                          )}
                        />
                        <Line
                          type="monotone"
                          dataKey={kpi}
                          stroke={kpiColors[kpi]}
                          strokeWidth={2}
                          dot={{ r: 3, fill: kpiColors[kpi] }}
                          activeDot={{ r: 5 }}
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              ))}
            </div>

            {/* Posts Table */}
            <div className="mt-6 bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
              <div className="p-4 border-b border-gray-200">
                <h3 className="font-semibold text-gray-900">{t('analytics:trends.postsInDetail')}</h3>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Post</th>
                      <th className="px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase">{t('analytics:metrics.reachShort')}</th>
                      <th className="px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase">{t('analytics:metrics.likes')}</th>
                      <th className="px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase">{t('analytics:metrics.commentsShort')}</th>
                      <th className="px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase">{t('analytics:metrics.saves')}</th>
                      <th className="px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase">{t('analytics:metrics.shares')}</th>
                      <th className="px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase">{t('analytics:metrics.engagementRateShort')}</th>
                      <th className="px-3 py-3 text-center text-xs font-medium text-gray-500 uppercase">{t('analytics:metrics.qualityShort')}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {recentPosts.slice().reverse().map((post) => (
                      <tr key={post.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-3">
                            {post.thumbnailUrl && (
                              <a href={post.permalink} target="_blank" rel="noopener noreferrer">
                                <img
                                  src={post.thumbnailUrl}
                                  alt=""
                                  className="w-10 h-10 rounded object-cover hover:opacity-80 transition-opacity"
                                />
                              </a>
                            )}
                            <div className="min-w-0">
                              <p className="text-sm text-gray-900 truncate max-w-[200px]">
                                {post.caption?.slice(0, 40) || t('analytics:noText')}
                              </p>
                              <p className="text-xs text-gray-500">
                                {new Date(post.timestamp).toLocaleDateString('de-DE')} • {post.ageInDays}d
                              </p>
                            </div>
                          </div>
                        </td>
                        <td className="px-3 py-3 text-right text-sm text-gray-700">
                          {post.reach.toLocaleString('de-DE')}
                        </td>
                        <td className="px-3 py-3 text-right text-sm text-gray-700">
                          {post.likeCount}
                        </td>
                        <td className="px-3 py-3 text-right text-sm text-gray-700">
                          {post.commentsCount}
                        </td>
                        <td className="px-3 py-3 text-right text-sm text-gray-700">
                          {post.saved}
                        </td>
                        <td className="px-3 py-3 text-right text-sm text-gray-700">
                          {post.shares}
                        </td>
                        <td className="px-3 py-3 text-right">
                          <span className={`text-sm font-medium px-2 py-0.5 rounded ${
                            post.engagementRate >= 10 ? 'bg-green-50 text-green-600' :
                            post.engagementRate >= 5 ? 'bg-blue-50 text-blue-600' :
                            post.engagementRate >= 2 ? 'bg-amber-50 text-amber-600' :
                            'bg-gray-50 text-gray-600'
                          }`}>
                            {post.engagementRate.toFixed(2)}%
                          </span>
                        </td>
                        <td className="px-3 py-3 text-center">
                          <span className={`text-lg font-bold ${
                            post.qualityScore >= 80 ? 'text-green-600' :
                            post.qualityScore >= 60 ? 'text-blue-600' :
                            post.qualityScore >= 40 ? 'text-amber-600' :
                            'text-gray-500'
                          }`}>
                            {post.qualityScore}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
